import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import AvailableStorages from '../transaction/components/AvailableStorages';
import AvailableDrugs from '../transaction/components/AvailableDrugs';
import { StorageType } from '../../application/types/storageType';
import { StorageLocationType } from '../../application/types/storageLocation';
import { TransactionReport } from '../../application/types/transactionReport';
import ReportService from './services/reportService';
import { TransactionType } from '../../application/types/transactionType';
import { useGlobalStore } from '../../application/store/store';
import { useSecureKey } from '../../stores/secureKeyStore';
import { ReAuthenticationModal } from '../../components/SecureKey/ReAuthenticationModal';
import { SecureKeyStatus } from '../../components/SecureKey/SecureKeyStatus';
import SecurityIcon from '@mui/icons-material/Security';

const StorageDrugReport = () => {
    const [transactions, setTransactions] = useState<TransactionReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const reportService = new ReportService();
    const [searchTermStorage, setSearchTermStorage] = useState<string>('');
    const [searchTermDrugs, setSearchTermDrugs] = useState<string>('');
    const [showReAuthModal, setShowReAuthModal] = useState(false);

    const {
        selectedStorage,
        setSelectedStorage,
        selectedDrug,
        setSelectedDrug,
        resetSelectedDrug,
        resetAvailableStorage,
    } = useGlobalStore();

    // Secure key hooks
    const { secureKey, hasActiveKey } = useSecureKey();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Date range state
    const [fromDate, setFromDate] = useState(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date(
            today.getTime() - 30 * 24 * 60 * 60 * 1000
        );
        return thirtyDaysAgo.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [dateValidationError, setDateValidationError] = useState<
        string | null
    >(null);

    useEffect(() => {
        resetSelectedDrug();
        resetAvailableStorage();
        setSearchTermDrugs('');
        setSearchTermStorage('');
        return () => {
            resetSelectedDrug();
            resetAvailableStorage();
            setSearchTermStorage('');
        };
    }, []);

    useEffect(() => {
        const fetchTransactions = async () => {
            if (
                selectedStorage &&
                selectedDrug &&
                fromDate &&
                toDate &&
                !dateValidationError
            ) {
                setLoading(true);
                setError(null);
                try {
                    const data = await reportService.getTransactionReport(
                        selectedStorage.id,
                        selectedDrug.id,
                        fromDate + 'T00:00:00.000Z',
                        toDate + 'T23:59:59.999Z',
                        secureKey || undefined // Pass secure key if available
                    );
                    setTransactions(data);
                } catch (err) {
                    setError(
                        'Det gick inte hämta rapporten. Om felet kvarstår kontakta administratör.'
                    );
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTransactions();
    }, [
        selectedStorage,
        selectedDrug,
        fromDate,
        toDate,
        dateValidationError,
        secureKey,
    ]);

    const getTransactionTypeText = (transaction: TransactionReport): string => {
        switch (transaction.transactionType) {
            case 0:
                return 'Uttag';
            case 1:
                return 'Påfyllning';
            case 2:
                return 'Inventering';
            case 3:
                return 'Uttag till patient';
            case 4:
                return transaction.comingFrom
                    ? `Från ${transaction.comingFrom}`
                    : 'Från läkemedelsskåp';
            case 5:
                return 'Skiftinventering';
            case 6:
                return 'Kassering';
            case 7:
                return transaction.goingTo
                    ? `Tillförd ${transaction.goingTo}`
                    : 'Tillförsel akutväska';
            case 8:
                return 'Kassera';
            case 9:
                // Show source when viewing from OR, destination when viewing from storage
                return transaction.comingFrom
                    ? `Överfört från ${transaction.comingFrom}`
                    : transaction.goingTo
                      ? `Överfört till ${transaction.goingTo}`
                      : 'Överföring';
            case 10:
                // Show destination when viewing from OR, source when viewing from storage
                return transaction.goingTo
                    ? `Retur till ${transaction.goingTo}`
                    : transaction.comingFrom
                      ? `Retur från ${transaction.comingFrom}`
                      : 'Retur';
            case 11:
                return selectedStorage?.type ===
                    StorageLocationType.OperatingRoom
                    ? 'Förbrukat'
                    : 'Förbrukat i operationssal';
            default:
                return 'Okänd';
        }
    };

    const getPlusOrMinusSign = (
        transactionType: number,
        changedQuantity: number
    ): string => {
        const isOperatingRoom =
            selectedStorage?.type === StorageLocationType.OperatingRoom;

        switch (transactionType) {
            case 0:
                return '-' + changedQuantity;
            case 1:
                return '+' + changedQuantity;
            case 2:
                return changedQuantity.toString();
            case 3:
                return '-' + changedQuantity;
            case 4:
                return '+' + changedQuantity;
            case 5:
                return changedQuantity.toString();
            case 6:
                return '-' + changedQuantity;
            case 7:
                return '-' + changedQuantity;
            case 8:
                return '-' + changedQuantity;
            case 9:
                // Transfer to operating room: + for OR (gains), - for storage (loses)
                return isOperatingRoom
                    ? '+' + changedQuantity
                    : '-' + changedQuantity;
            case 10:
                // Return from operating room: - for OR (loses), + for storage (gains)
                return isOperatingRoom
                    ? '-' + changedQuantity
                    : '+' + changedQuantity;
            case 11:
                // Consumption in operating room: always - (consumed)
                return '-' + changedQuantity;
            default:
                return 'Okänd';
        }
    };

    const formatDateTime = (dateString: string): string => {
        return DateTime.fromISO(dateString).toFormat('yyyy-MM-dd HH:mm');
    };

    const validateDateRange = (from: string, to: string) => {
        if (from && to && new Date(from) > new Date(to)) {
            setDateValidationError(
                'Från-datum kan inte vara senare än till-datum'
            );
        } else {
            setDateValidationError(null);
        }
    };

    const getPatientDataDisplay = (transaction: TransactionReport): string => {
        // If patient data exists and we have a secure key, show it
        if (transaction.patientData && hasActiveKey) {
            return `${transaction.patientData.firstname} ${transaction.patientData.lastname} (${transaction.patientData.personnummer})`;
        }

        // If patient data exists but no secure key (or secure key invalid), show hidden indicator
        if (transaction.hasPatientData && !transaction.patientData) {
            return '***';
        }

        // No patient data available for this transaction
        return '';
    };

    const getMedicalRecordIdDisplay = (
        transaction: TransactionReport
    ): string => {
        // If medical record ID exists and we have a secure key, show it
        if (transaction.medicalRecordId && hasActiveKey) {
            return transaction.medicalRecordId;
        }

        // If medical record ID exists but no secure key (or secure key invalid), show hidden indicator
        if (transaction.hasMedicalRecordId && !transaction.medicalRecordId) {
            return '***';
        }

        // No medical record ID available for this transaction
        return '';
    };

    const handleSelectStorage = (storage: any) => {
        setSelectedStorage(storage);
        setSearchTermStorage(storage?.name ?? '');
    };

    const handleSelectDrug = (drug: any) => {
        setSelectedDrug(drug);
        setSearchTermDrugs(
            drug?.drugName
                ? `${drug.drugName} (${drug.concentration ?? ''} ${drug.concentrationUnit ?? ''}, ${drug.quantity ?? ''}ml)`
                : ''
        );
    };

    const handleFromDateChange = (newFromDate: string) => {
        setFromDate(newFromDate);
        validateDateRange(newFromDate, toDate);
    };

    const handleToDateChange = (newToDate: string) => {
        setToDate(newToDate);
        validateDateRange(fromDate, newToDate);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAuthenticateClick = () => {
        setShowReAuthModal(true);
    };

    const handleAuthenticationSuccess = () => {
        setShowReAuthModal(false);
        // Transactions will be refetched automatically due to secureKey dependency
    };

    const handleReAuthenticate = () => {
        setShowReAuthModal(true);
    };

    const paginatedData = transactions
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper className="p-3">
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Förbrukningsrapport
            </Typography>

            {/* Secure Key Authentication Section - Compact */}
            <Box className="mb-3 flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                <Box className="flex items-center gap-2">
                    <SecurityIcon color="primary" fontSize="small" />
                    {hasActiveKey ? (
                        <SecureKeyStatus
                            onReAuthenticate={handleReAuthenticate}
                            showDetails={false}
                            compact={true}
                        />
                    ) : (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2">
                                <Box component="span" fontWeight="medium">
                                    Åtkomst till patientdata:{' '}
                                </Box>
                                <Box component="span" color="text.secondary">
                                    Ej autentiserad.
                                </Box>
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                Du kan se förbrukning men utan patientdata eller
                                ärendenummer. *** indikerar att data finns, men
                                visas inte utan autentiering.
                            </Typography>
                        </Box>
                    )}
                </Box>
                {!hasActiveKey && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleAuthenticateClick}
                        startIcon={<SecurityIcon fontSize="small" />}
                    >
                        Autentisera
                    </Button>
                )}
            </Box>

            <Box className="mb-4 p-4 bg-gray-50 rounded-lg">
                <Box className="flex gap-4 flex-wrap">
                    {/* Date Range Selection */}
                    <div className="flex flex-col min-w-[200px]">
                        <h2 className="text-sm font-medium mb-2">
                            Datumintervall:
                        </h2>
                        <Box
                            className="flex gap-2 flex-col sm:flex-row"
                            sx={{ height: '56px', alignItems: 'flex-end' }}
                        >
                            <TextField
                                label="Från datum"
                                type="date"
                                value={fromDate}
                                onChange={(e) =>
                                    handleFromDateChange(e.target.value)
                                }
                                size="medium"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                                className="min-w-[150px]"
                                sx={{ height: '56px' }}
                                error={!!dateValidationError}
                            />
                            <TextField
                                label="Till datum"
                                type="date"
                                value={toDate}
                                onChange={(e) =>
                                    handleToDateChange(e.target.value)
                                }
                                size="medium"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                                className="min-w-[150px]"
                                sx={{ height: '56px' }}
                                error={!!dateValidationError}
                            />
                        </Box>
                        {dateValidationError && (
                            <Alert severity="error" className="mt-2">
                                {dateValidationError}
                            </Alert>
                        )}
                    </div>

                    {/* Storage Selection */}
                    <div className="flex flex-col min-w-[250px]">
                        <h2 className="text-sm font-medium mb-2 sm:text-align-center">
                            Välj lagerställe:
                        </h2>
                        <AvailableStorages
                            label="Lagerställe"
                            searchTerm={searchTermStorage}
                            setSearchTerm={setSearchTermStorage}
                            storageType={StorageType.storage}
                            transactionType={TransactionType.Crosscheck}
                            handleSelect={handleSelectStorage}
                            handleSelectEmergency={() => {}}
                        />
                    </div>

                    {/* Drug Selection (Only Shows If Storage Is Selected) */}
                    {selectedStorage && (
                        <div className="flex flex-col min-w-[250px]">
                            <h2 className="text-sm font-medium mb-2">
                                Välj läkemedel:
                            </h2>
                            <AvailableDrugs
                                searchTerm={searchTermDrugs}
                                setSearchTerm={setSearchTermDrugs}
                                handleSelect={handleSelectDrug}
                            />
                        </div>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" style={{ marginBottom: '20px' }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </div>
            ) : (
                selectedStorage &&
                selectedDrug &&
                transactions.length > 0 && (
                    <TableContainer component={Paper}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Ändrad mängd</TableCell>
                                    <TableCell>Saldo före</TableCell>
                                    <TableCell>Saldo efter</TableCell>
                                    <TableCell>Signerad av</TableCell>
                                    <TableCell>Dubbelsignatur</TableCell>
                                    <TableCell>Patient</TableCell>
                                    <TableCell>Ärendenummer</TableCell>
                                    <TableCell>Anteckning</TableCell>
                                    <TableCell>Avvikelse</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData
                                    .sort(
                                        (a, b) =>
                                            new Date(b.date).getTime() -
                                            new Date(a.date).getTime()
                                    )
                                    .map((transaction) => (
                                        <TableRow
                                            className="odd:bg-gray-100"
                                            key={transaction.id}
                                        >
                                            <TableCell>
                                                {formatDateTime(
                                                    transaction.date
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getTransactionTypeText(
                                                    transaction
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getPlusOrMinusSign(
                                                    transaction.transactionType,
                                                    transaction.changedQuantity
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    transaction.saldoBeforeTransaction
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    transaction.saldoAfterTransaction
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {transaction.signature
                                                    ? `${transaction.signature.firstname} ${transaction.signature.lastname}`
                                                    : 'Saknas'}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.doubleSignature
                                                    ? `${transaction.doubleSignature.firstname} ${transaction.doubleSignature.lastname}`
                                                    : ''}
                                            </TableCell>
                                            <TableCell>
                                                {getPatientDataDisplay(
                                                    transaction
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getMedicalRecordIdDisplay(
                                                    transaction
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.description || ''}
                                            </TableCell>
                                            <TableCell>
                                                {transaction
                                                    .drugQuantityDeviation
                                                    ?.quantityMismatch
                                                    ? transaction
                                                          .drugQuantityDeviation
                                                          .quantityMismatch ===
                                                      0
                                                        ? ''
                                                        : transaction
                                                                .drugQuantityDeviation
                                                                .quantityMismatch >
                                                            0
                                                          ? `${transaction.drugQuantityDeviation.quantityMismatch} st för mycket`
                                                          : `${Math.abs(transaction.drugQuantityDeviation.quantityMismatch)} st för lite`
                                                    : ''}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={transactions.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Antal poster:"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                            }
                        />
                    </TableContainer>
                )
            )}

            {!loading &&
                transactions.length === 0 &&
                selectedStorage &&
                selectedDrug && (
                    <Alert severity="info">Inga transaktioner hittades</Alert>
                )}

            {/* Re-authentication Modal */}
            <ReAuthenticationModal
                open={showReAuthModal}
                onClose={() => setShowReAuthModal(false)}
                onSuccess={handleAuthenticationSuccess}
                title="Autentisera för patientdata"
                description="Du behöver logga in för att komma åt patientuppgifter.
All aktivitet under sessionen registreras och kan komma att granskas vid misstanke om obehörig åtkomst."
            />
        </Paper>
    );
};

export default StorageDrugReport;
